export type ClipboardFormat =
  | "internal"
  | "element-shortcut"
  | "tsv"
  | "html"
  | "csv"
  | "unknown";

export const detectClipboardFormat = (
  text: string,
  html?: string,
): ClipboardFormat => {
  try {
    const parsed = JSON.parse(text);
    if (parsed?.feature === "table-cells-shortcut") {
      return "internal";
    }
    // A copied element (see `copyEntityToClipboard`, feature: "shortcut").
    // Checked before the html test below so its readable html (which may
    // contain <table>/<tr> when the element itself renders a table) isn't
    // mis-detected as tabular paste data.
    if (
      parsed?.feature === "shortcut" &&
      typeof parsed.entityEntryRootId === "string"
    ) {
      return "element-shortcut";
    }
  } catch {
    // Not JSON, continue detection
  }

  // Case-insensitive match — Word/Excel paste markup often comes through as
  // upper-case (`<TABLE`, `<TR`).
  if (html && /<table|<tr/i.test(html)) {
    return "html";
  }

  // Rich text that is not a table (a copied text element, a Docs or web
  // selection) is one value. Its plain-text flavour only looked tabular:
  // two copied paragraphs with two commas each were split across cells as
  // CSV (QA E14). Spreadsheets always send their grid as an HTML table.
  if (html?.trim()) {
    return "unknown";
  }

  if (text.includes("\t")) {
    return "tsv";
  }

  if (text.includes(",") && text.includes("\n")) {
    // Check that lines have a consistent comma count to avoid false positives on prose
    const lines = text.split("\n").filter((l) => l.length > 0);
    if (lines.length >= 2) {
      const commaCounts = lines.map((l) => (l.match(/,/g) || []).length);
      const firstCount = commaCounts[0];
      const isConsistent =
        firstCount > 0 && commaCounts.every((c) => c === firstCount);
      if (isConsistent) {
        return "csv";
      }
    }
  }

  return "unknown";
};

export const parseTSV = (text: string): string[][] => {
  const lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  return lines
    .split("\n")
    .filter((line) => line.length > 0)
    .map((line) => line.split("\t"));
};

/**
 * One cell of an external grid. Plain text by default; a cell that carried a
 * hyperlink (Excel / Google Sheets write `<td><a href="…">text</a></td>` into
 * their text/html clipboard part) also brings `html` — an inline fragment
 * that is already safe to store as cell content: text escaped, every tag but
 * the anchor flattened, href scheme-checked, foreign attributes dropped.
 */
export interface IExternalLinkCell {
  text: string;
  html: string;
}

export type ExternalCellValue = string | IExternalLinkCell;

export const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/** Inline HTML for one external cell — escaped text, or the prebuilt link. */
export const externalCellToHtml = (value: ExternalCellValue): string =>
  typeof value === "string" ? escapeHtml(value) : value.html;

/**
 * For a 1x1 external grid pasted onto a caret already inside a cell: the inline
 * HTML to insert there, or `null` to leave the paste to Froala.
 *
 * A lone plain-text cell is left alone deliberately — Froala inserts it at the
 * caret, which is what someone typing mid-cell expects. A lone cell that
 * carried a hyperlink cannot take that route: Excel and Google Sheets wrap even
 * a single copied cell in a full `<table>`, and Froala's paste cleaner keeps
 * that wrapper, so the anchor arrives nested in markup a cell cannot hold and
 * is flattened straight back to the plain text this whole path exists to
 * avoid. Same href loss the grid paths fixed, one cell at a time.
 * Monday 12977734438.
 */
export const singleExternalCellCaretHtml = (
  data: ExternalCellValue[][],
): string | null => {
  if (data.length !== 1 || data[0]?.length !== 1) return null;
  const value = data[0][0];
  return typeof value === "string" ? null : value.html;
};

// Only web/mail links survive a paste. Everything else (javascript:, file:
// links to the local workbook, "#Sheet2!A1" intra-sheet refs, relative paths)
// is meaningless or dangerous inside a story and falls back to plain text.
const PASTED_LINK_HREF = /^(?:https?:|mailto:)/i;

// Inline fragment for one pasted cell: text nodes escaped, `<a href>` kept as
// a bare `<a href target="_blank">` (the shape the text editor's own link
// dialog inserts, minus its popup/analytics metadata), all other elements
// flattened to their children. Returns null when no link was kept, so a cell
// without a usable link stays a plain string.
const cellToLinkHtml = (cell: Element): string | null => {
  let keptLink = false;
  const walk = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return escapeHtml(node.textContent ?? "");
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return "";
    const el = node as Element;
    const text = el.textContent ?? "";
    if (el.tagName === "A") {
      const href = el.getAttribute("href")?.trim() ?? "";
      if (PASTED_LINK_HREF.test(href) && text.trim()) {
        keptLink = true;
        return `<a href="${escapeHtml(href)}" target="_blank">${escapeHtml(text)}</a>`;
      }
      return escapeHtml(text);
    }
    return Array.from(el.childNodes).map(walk).join("");
  };
  const html = Array.from(cell.childNodes).map(walk).join("").trim();
  return keptLink ? html : null;
};

// A row that sits inside another table's cell belongs to a nested table, not
// to the grid being pasted. `querySelectorAll` is document-wide and descends
// through nesting, so without this both the outer and the inner rows come
// back — and the outer row also picks up the inner row's cells, repeating
// every nested value. Rich sources (newsletter and web-page markup built out
// of layout tables) hit this constantly; Excel and Sheets never nest.
const isNestedRow = (row: Element): boolean =>
  !!row.parentElement?.closest("td, th");

const occupyKey = (row: number, col: number): string => `${row}:${col}`;

const positiveSpan = (value: number): number =>
  Number.isFinite(value) && value > 0 ? value : 1;

const readCellValue = (cell: Element): ExternalCellValue => {
  const text = cell.textContent?.trim() ?? "";
  const linkHtml = cell.querySelector("a[href]") ? cellToLinkHtml(cell) : null;
  return linkHtml ? { text, html: linkHtml } : text;
};

const densifyRow = (row: ExternalCellValue[]): ExternalCellValue[] => {
  const dense: ExternalCellValue[] = [];
  for (let c = 0; c < row.length; c++) dense.push(row[c] ?? "");
  return dense;
};

// Spreadsheet HTML writes a merged cell as one <td colspan>/<td rowspan>.
// The paste grid is a dense matrix of 1×1 slots (new tables always store
// colSpan/rowSpan as 1), so covered coordinates have to become empty cells
// or the html path wins with the wrong geometry.
const paintMergedCell = (
  grid: ExternalCellValue[][],
  occupied: Set<string>,
  row: number,
  col: number,
  rowSpan: number,
  colSpan: number,
  value: ExternalCellValue,
): boolean => {
  for (let dr = 0; dr < rowSpan; dr++) {
    const r = row + dr;
    if (!grid[r]) grid[r] = [];
    for (let dc = 0; dc < colSpan; dc++) {
      const c = col + dc;
      const key = occupyKey(r, c);
      if (occupied.has(key)) return false;
      occupied.add(key);
      if (dr === 0 && dc === 0) grid[r][c] = value;
      else if (grid[r][c] === undefined) grid[r][c] = "";
    }
  }
  return true;
};

export const parseHTML = (html: string): ExternalCellValue[][] => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const rows = doc.querySelectorAll("tr");
  const grid: ExternalCellValue[][] = [];
  const occupied = new Set<string>();

  let logicalRow = 0;
  for (let ri = 0; ri < rows.length; ri++) {
    if (isNestedRow(rows[ri])) continue;
    // Only this row's own cells — `closest("tr")` skips any that belong to a
    // table nested inside one of them. Such a host cell is still kept, and
    // flattens to the nested table's text like any other markup would.
    const cells = Array.from(rows[ri].querySelectorAll("td, th")).filter(
      (cell) => cell.closest("tr") === rows[ri],
    );

    let col = 0;
    for (let ci = 0; ci < cells.length; ci++) {
      while (occupied.has(occupyKey(logicalRow, col))) col++;
      const cell = cells[ci] as HTMLTableCellElement;
      const colSpan = positiveSpan(cell.colSpan);
      const placed = paintMergedCell(
        grid,
        occupied,
        logicalRow,
        col,
        positiveSpan(cell.rowSpan),
        colSpan,
        readCellValue(cell),
      );
      // Overlapping spans are not a grid we can trust — callers with a TSV
      // part fall through rather than paste a collided layout.
      if (!placed) return [];
      col += colSpan;
    }
    logicalRow++;
  }

  return grid.filter((row) => row && row.length > 0).map(densifyRow);
};

// Excel in most of Europe writes `;`-separated "CSV". Counting separators
// outside quotes is enough to tell the two dialects apart, and it keeps a file
// full of `"Acme, Inc."` from being mistaken for comma-separated.
const detectDelimiter = (normalized: string): "," | ";" => {
  let commas = 0;
  let semicolons = 0;
  let inQuotes = false;
  for (let i = 0; i < normalized.length; i++) {
    const char = normalized[i];
    if (char === '"') {
      // Skip an escaped quote rather than toggling twice.
      if (inQuotes && normalized[i + 1] === '"') i++;
      else inQuotes = !inQuotes;
      continue;
    }
    if (inQuotes) continue;
    if (char === ",") commas++;
    else if (char === ";") semicolons++;
  }
  return semicolons > commas ? ";" : ",";
};

export const parseCSV = (text: string): string[][] => {
  const result: string[][] = [];
  let currentRow: string[] = [];
  let currentField = "";
  let inQuotes = false;

  const normalized = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const delimiter = detectDelimiter(normalized);

  for (let i = 0; i < normalized.length; i++) {
    const char = normalized[i];

    if (inQuotes) {
      if (char === '"') {
        if (i + 1 < normalized.length && normalized[i + 1] === '"') {
          // Escaped quote
          currentField += '"';
          i++;
        } else {
          // End of quoted field
          inQuotes = false;
        }
      } else {
        currentField += char;
      }
    } else {
      if (char === '"' && currentField.length === 0) {
        inQuotes = true;
      } else if (char === delimiter) {
        currentRow.push(currentField);
        currentField = "";
      } else if (char === "\n") {
        currentRow.push(currentField);
        currentField = "";
        result.push(currentRow);
        currentRow = [];
      } else {
        currentField += char;
      }
    }
  }

  // Handle last field/row
  if (currentField.length > 0 || currentRow.length > 0) {
    currentRow.push(currentField);
    result.push(currentRow);
  }

  return result;
};

export const stripHtmlTags = (html: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  return doc.body.textContent ?? "";
};
